"use client";

import type { SessionRecordingOptions } from "posthog-js";
import { useEffect, useRef } from "react";

import { useConsent } from "../consent";
import { AnalyticsProvider } from "./AnalyticsProvider";
import { initializePostHog, posthog } from "./posthog.client";

export interface ConsentGatedAnalyticsProviderProps {
  children: React.ReactNode;
  /** PostHog project key. Missing or empty means analytics never starts. */
  apiKey?: string;
  apiHost?: string;
  debug?: boolean;
  /** Reported with each pageview. */
  currentLocale?: string;
  /** `<app>@<commit>`, from NEXT_PUBLIC_APP_RELEASE. Stamped on every event. */
  release?: string;
  appVersion?: string;
  /**
   * Replay privacy for this app. See `PostHogConfig.sessionRecording`.
   * Pass a stable reference (a module-level constant): this sits in the effect's
   * dependencies, and a fresh object per render would re-run the opt-in every render.
   */
  sessionRecording?: SessionRecordingOptions;
  /** Keep autocapture from reading element text. See `PostHogConfig.maskAllText`. */
  maskAllText?: boolean;
  /**
   * Start with replay off. Read at init and only then, so an app whose current
   * page must never be recorded passes the live answer for the page it is on.
   * See `PostHogConfig.disableSessionRecording` for why a later stop is not
   * enough.
   */
  disableSessionRecording?: boolean;
}

/**
 * PostHog, started only once the visitor has allowed the analytics category,
 * and stopped again if they take it back.
 *
 * The gate is the part that has to be shared, not just the consent store: the
 * init-once ref, the opt-in on a later grant and the reset-then-opt-out on
 * withdrawal are the same on any site. Only the key, the host and the locale
 * are the app's. A site wiring plain `AnalyticsProvider` instead starts PostHog
 * for everyone, which is what "no consent gate" looks like in code.
 *
 * Requires a `ConsentProvider` above it, which is what asks the question.
 */
export function ConsentGatedAnalyticsProvider({
  children,
  apiKey,
  apiHost,
  debug,
  currentLocale,
  release,
  appVersion,
  sessionRecording,
  maskAllText,
  disableSessionRecording,
}: ConsentGatedAnalyticsProviderProps) {
  const { allows } = useConsent();
  const started = useRef(false);

  const analyticsAllowed = allows("analytics");

  useEffect(() => {
    // No key means initializePostHog returns without calling init. Marking
    // started anyway would send the withdrawal branch into reset() and
    // opt_out_capturing() on an instance that was never initialised, which
    // posthog-js answers with a console warning per navigation.
    if (!apiKey) return;

    if (analyticsAllowed) {
      const justStarted = !started.current;
      // Gate the CALL, not the capturing: posthog-js writes its cookie on
      // init. The ref keeps a second init from running.
      if (justStarted) {
        started.current = true;
        // Not deferred to window `load`: posthog-js DROPS pre-init captures,
        // so anything fired between hydration and `load` vanishes silently.
        initializePostHog({
          apiKey,
          apiHost,
          debug,
          release,
          appVersion,
          sessionRecording,
          maskAllText,
          disableSessionRecording,
        });
      }
      // The way back from a withdrawal: opt-out persists, so a later yes has to
      // say so explicitly. No `$opt_in` event — this runs on every grant, not
      // once, and posthog-js captures one by default.
      posthog.opt_in_capturing({ captureEventName: false });
      // A LATER grant only. The withdrawal below calls reset(), which clears
      // persistence and the super properties with it, and init does not run a
      // second time — but on the first grant init has already registered them.
      if (!justStarted && release)
        posthog.register({ app_release: release, app_version: appVersion });
      return;
    }

    // Withdrawn. Gating init is not enough once it HAS run: the instance stays
    // live, the pageview tracker is mounted unconditionally, and the next
    // navigation re-captures and rewrites the cookie the consent panel just
    // deleted.
    if (!started.current) return;
    // reset() FIRST. It clears consent settings along with the identity, so
    // opting out before it would be wiped — posthog-js documents this exact
    // ordering on reset().
    posthog.reset();
    posthog.opt_out_capturing();
  }, [
    analyticsAllowed,
    apiKey,
    apiHost,
    debug,
    release,
    appVersion,
    sessionRecording,
    maskAllText,
    disableSessionRecording,
  ]);

  return (
    <AnalyticsProvider currentLocale={currentLocale}>
      {children}
    </AnalyticsProvider>
  );
}
